import { useAppSelector } from "../../redux/hooks";
import { Link } from "@tanstack/react-router";
import { apiClient } from "../../zodios/client";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import DismissibleBanner from "../core/basic/DismissibleBanner";
import type { SpApiRegionStatus } from "../../zodios/apiDefinitions/api/spApiCredentialCheck";

const useSpApiCredentialCheck = (
  params: NonNullable<Parameters<typeof apiClient.checkSpApiCredential>[0]>["queries"],
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ["spApiCredentialCheck", params],
    queryFn: async () => await apiClient.checkSpApiCredential({ queries: params }),
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    ...options,
  });

const linkComponents = {
  amazonConnectLink: <span style={{ textDecoration: "underline" }} />,
};

const SpApiTokenExpirationBanner = () => {
  const { t } = useTranslation(["onboarding"]);
  const onboardData = useAppSelector((state) => state.amz_onboard);

  const isSpApiConnected = [
    onboardData.amazon_spapi_access,
    onboardData.eu_amazon_spapi_access,
    onboardData.fe_amazon_spapi_access,
  ].includes("connected");

  // Deliberately not scoped to the selected marketplace: a region the switcher
  // is not on needs re-authorizing just as much, and the customer has no way to
  // know which one unless the banner names it.
  const credentialCheckQuery = useSpApiCredentialCheck({}, { enabled: isSpApiConnected });
  const check = credentialCheckQuery.data;

  const regionLabels: Record<string, string> = {
    na: t(($) => $.onboarding.regions["North America"]),
    eu: t(($) => $.onboarding.regions.Europe),
    fe: t(($) => $.onboarding.regions["Far East"]),
  };

  const regionsWith = (status: SpApiRegionStatus) =>
    Object.keys(check?.regions ?? {})
      .filter((region) => check?.regions[region]?.status === status)
      .sort();

  const namesOf = (regions: string[]) => regions.map((region) => regionLabels[region] ?? region).join(", ");

  const expiredRegions = regionsWith("needs_reauth");
  if (expiredRegions.length > 0) {
    return (
      <DismissibleBanner
        storageKey={`dismiss_sp_api_credential_expiration_banner_${expiredRegions.join("_")}`}
        dismissForDays={7}
      >
        <Link to="/user/amzsummary" className="text-white text-decoration-none px-2">
          <Trans
            t={t}
            i18nKey={($) =>
              $.onboarding[
                "Your organization's Amazon Selling Partner API tokens for {regions} are now expired. Please reauthorize by visiting the <amazonConnectLink>Amazon Connect</amazonConnectLink> page to ensure your account's historical data is kept up to date."
              ]
            }
            values={{ regions: namesOf(expiredRegions) }}
            components={linkComponents}
          />
        </Link>
      </DismissibleBanner>
    );
  }

  const outdatedRegions = regionsWith("outdated");
  if (outdatedRegions.length > 0) {
    const roles = check?.sp_api_missing_roles.join(", ") ?? "";
    const regions = namesOf(outdatedRegions);
    return (
      // Keyed by the regions it names, so dismissing it for one region cannot
      // hide a second region that falls behind later.
      <DismissibleBanner
        storageKey={`dismiss_sp_api_grant_outdated_banner_${outdatedRegions.join("_")}`}
        dismissForDays={7}
      >
        <Link to="/user/amzsummary" className="text-white text-decoration-none px-2">
          {roles ?
            <Trans
              t={t}
              i18nKey={($) =>
                $.onboarding[
                  "Trellis now needs additional Amazon Selling Partner permissions ({roles}) for {regions}. Please re-authorize on the <amazonConnectLink>Amazon Connect</amazonConnectLink> page so your account's selling data stays complete."
                ]
              }
              values={{ roles, regions }}
              components={linkComponents}
            />
          : <Trans
              t={t}
              i18nKey={($) =>
                $.onboarding[
                  "Trellis now needs additional Amazon Selling Partner permissions for {regions}. Please re-authorize on the <amazonConnectLink>Amazon Connect</amazonConnectLink> page so your account's selling data stays complete."
                ]
              }
              values={{ regions }}
              components={linkComponents}
            />
          }
        </Link>
      </DismissibleBanner>
    );
  }

  return null;
};

export default SpApiTokenExpirationBanner;
